'use client';

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { setSuperAdminTenantOverride } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface PerusahaanOption {
  id: number;
  namaPerusahaan: string;
  statusPlatform?: string;
}

interface CabangOption {
  id: number;
  namaCabang: string;
  kodeCabang?: string;
}

interface SuperAdminContextType {
  selectedPerusahaan: PerusahaanOption | null;
  selectedCabang: CabangOption | null;
  selectPerusahaan: (p: PerusahaanOption | null) => void;
  selectCabang: (c: CabangOption | null) => void;
  clearSelection: () => void;
}

const SuperAdminContext = createContext<SuperAdminContextType | undefined>(undefined);

// Snapshot pilihan tenant (id + nama) dipersist agar dropdown tetap terpilih
// setelah window.location.reload() yang dipicu switcher di Sidebar.
const SA_SELECTION_KEY = 'superadmin_tenant_selection';

interface PersistedSelection {
  perusahaan: PerusahaanOption | null;
  cabang: CabangOption | null;
}

function readPersistedSelection(): PersistedSelection {
  if (typeof window === 'undefined') return { perusahaan: null, cabang: null };
  try {
    const raw = window.localStorage.getItem(SA_SELECTION_KEY);
    if (!raw) return { perusahaan: null, cabang: null };
    const parsed = JSON.parse(raw);
    return {
      perusahaan: parsed?.perusahaan ?? null,
      cabang: parsed?.cabang ?? null,
    };
  } catch {
    return { perusahaan: null, cabang: null };
  }
}

function writePersistedSelection(sel: PersistedSelection) {
  if (typeof window === 'undefined') return;
  try {
    if (sel.perusahaan) {
      window.localStorage.setItem(SA_SELECTION_KEY, JSON.stringify(sel));
    } else {
      window.localStorage.removeItem(SA_SELECTION_KEY);
    }
  } catch { /* localStorage unavailable — abaikan */ }
}

export const useSuperAdmin = () => {
  const ctx = useContext(SuperAdminContext);
  if (!ctx) throw new Error('useSuperAdmin must be used within SuperAdminProvider');
  return ctx;
};

export const SuperAdminProvider = ({ children }: { children: ReactNode }) => {
  const { user, isSuperAdmin } = useAuth();
  const isOwner = (user?.roleNama ?? '').toLowerCase() === 'owner';

  const [selectedPerusahaan, setSelectedPerusahaan] = useState<PerusahaanOption | null>(
    () => readPersistedSelection().perusahaan,
  );
  const [selectedCabang, setSelectedCabang] = useState<CabangOption | null>(
    () => readPersistedSelection().cabang,
  );

  const selectPerusahaan = useCallback((p: PerusahaanOption | null) => {
    setSelectedPerusahaan(p);
    setSelectedCabang(null);
    setSuperAdminTenantOverride(p?.id ?? null, null);
    writePersistedSelection({ perusahaan: p, cabang: null });
  }, []);

  const selectCabang = useCallback((c: CabangOption | null) => {
    setSelectedCabang(c);
    setSuperAdminTenantOverride(selectedPerusahaan?.id ?? null, c?.id ?? null);
    writePersistedSelection({ perusahaan: selectedPerusahaan, cabang: c });
  }, [selectedPerusahaan]);

  const clearSelection = useCallback(() => {
    setSelectedPerusahaan(null);
    setSelectedCabang(null);
    setSuperAdminTenantOverride(null, null);
    writePersistedSelection({ perusahaan: null, cabang: null });
  }, []);

  // Owner multi-tenant: kunci selectedPerusahaan ke tenant aktif JWT.
  // localStorage bisa stale setelah ganti perusahaan via Header CompanySwitcher.
  useEffect(() => {
    if (!isOwner || isSuperAdmin || !user?.perusahaanId) return;
    if (!selectedPerusahaan || selectedPerusahaan.id !== user.perusahaanId) {
      selectPerusahaan({
        id: user.perusahaanId,
        namaPerusahaan: user.namaPerusahaan ?? '',
      });
    }
  }, [isOwner, isSuperAdmin, user?.perusahaanId, user?.namaPerusahaan, selectedPerusahaan, selectPerusahaan]);

  return (
    <SuperAdminContext.Provider value={{ selectedPerusahaan, selectedCabang, selectPerusahaan, selectCabang, clearSelection }}>
      {children}
    </SuperAdminContext.Provider>
  );
};

export type { PerusahaanOption, CabangOption };
